import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Heart,
  Facebook,
  Instagram,
  Twitter,
  Youtube,
  Send,
  ShieldCheck,
  Mail,
  Phone,
  MapPin,
  X,
  Copy,
  Check,
  ExternalLink,
  MessageCircle
} from 'lucide-react';
import { PairUpLogo } from './common/PairUpLogo';

interface FooterProps {
  onNavClick: (tab: string) => void;
  onOpenSafety: () => void;
  onOpenMembership: () => void;
  onOpenAbout?: () => void;
  onOpenProfile?: (tab?: 'profile' | 'wallet') => void;
  currentUser?: string | null;
  onOpenAuth?: (mode: 'login' | 'signup') => void;
  onOpenPermissions?: () => void;
}

export const Footer: React.FC<FooterProps> = ({
  onNavClick,
  onOpenSafety,
  onOpenMembership,
  onOpenAbout,
  onOpenProfile,
  currentUser,
  onOpenAuth,
  onOpenPermissions,
}) => {
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [isContactOpen, setIsContactOpen] = useState(false);
  const [copiedEmail, setCopiedEmail] = useState(false);

  const officialEmail = 'pairuptoday@gmail.com';

  const handleCopyEmail = () => {
    navigator.clipboard.writeText(officialEmail);
    setCopiedEmail(true);
    setTimeout(() => setCopiedEmail(false), 2500);
  };

  const handleSubscribe = (e: React.FormEvent) => {
    e.preventDefault();
    if (email.trim()) {
      setSubscribed(true);
      setTimeout(() => {
        setSubscribed(false);
        setEmail('');
      }, 4000);
    }
  };

  return (
    <footer id="main-footer" className="bg-[#0e0a22] text-slate-300 pt-16 pb-12 border-t border-purple-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Main 4/5 Columns Grid matching screenshot */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10 lg:gap-8 pb-12 border-b border-slate-800/80">
          
          {/* Brand Col (4 cols) */}
          <div className="lg:col-span-4 space-y-4">
            <div className="flex items-center gap-2.5">
              <PairUpLogo className="w-11 h-11 shrink-0" />
              <div className="flex items-center tracking-tight">
                <span className="font-extrabold text-xl text-white">PairUp</span>
                <span className="font-extrabold text-xl text-pink-500">Today</span>
              </div>
            </div>

            <p className="text-xs sm:text-sm text-slate-400 leading-relaxed max-w-sm">
              Your trusted platform to find companionship for any occasion. Safe, secure and reliable.
            </p>

            {/* Social Links matching screenshot */}
            <div className="flex items-center gap-2.5 pt-2">
              <a
                href="https://www.facebook.com/share/19Xw6KFEKU/"
                target="_blank"
                rel="noopener noreferrer"
                id="footer-social-fb"
                className="w-9 h-9 rounded-full bg-slate-800/90 hover:bg-[#1877F2] text-slate-300 hover:text-white flex items-center justify-center transition-all cursor-pointer hover:scale-110 shadow-sm"
                title="Follow us on Facebook"
              >
                <Facebook className="w-4 h-4" />
              </a>
              <a
                href="https://www.instagram.com/pairuptoday?stkn=MXBtNXhreTBnMWhuNA=="
                target="_blank"
                rel="noopener noreferrer"
                id="footer-social-ig"
                className="w-9 h-9 rounded-full bg-slate-800/90 hover:bg-gradient-to-tr hover:from-amber-500 hover:via-pink-500 hover:to-purple-600 text-slate-300 hover:text-white flex items-center justify-center transition-all cursor-pointer hover:scale-110 shadow-sm"
                title="Follow us on Instagram (@pairuptoday)"
              >
                <Instagram className="w-4 h-4" />
              </a>
              <a
                href="#twitter"
                id="footer-social-tw"
                className="w-9 h-9 rounded-full bg-slate-800/90 hover:bg-pink-600 text-slate-300 hover:text-white flex items-center justify-center transition-all cursor-pointer"
                title="Twitter / X"
              >
                <Twitter className="w-4 h-4" />
              </a>
              <a
                href="#youtube"
                id="footer-social-yt"
                className="w-9 h-9 rounded-full bg-slate-800/90 hover:bg-pink-600 text-slate-300 hover:text-white flex items-center justify-center transition-all cursor-pointer"
                title="YouTube"
              >
                <Youtube className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Quick Links (2 cols) */}
          <div className="lg:col-span-2 space-y-3">
            <h4 className="font-bold text-white text-sm tracking-wider uppercase">
              Quick Links
            </h4>
            <ul className="space-y-2 text-xs sm:text-sm text-slate-400">
              <li>
                <button
                  onClick={() => onNavClick('home')}
                  className="hover:text-pink-400 transition-colors cursor-pointer"
                >
                  Home
                </button>
              </li>
              <li>
                <button
                  onClick={() => onNavClick('how-it-works')}
                  className="hover:text-pink-400 transition-colors cursor-pointer"
                >
                  How It Works
                </button>
              </li>
              <li>
                <button
                  onClick={() => onNavClick('browse')}
                  className="hover:text-pink-400 transition-colors cursor-pointer"
                >
                  Browse
                </button>
              </li>
              <li>
                <Link
                  to="/companion/register"
                  className="text-purple-300 hover:text-pink-300 font-bold transition-colors cursor-pointer flex items-center gap-1.5"
                >
                  <span>Become a Companion</span>
                  <span className="text-[10px] bg-gradient-to-r from-purple-600 to-pink-600 text-white font-extrabold px-1.5 py-0.5 rounded-full">
                    Earn ₹1L
                  </span>
                </Link>
              </li>
              <li>
                <button
                  onClick={onOpenMembership}
                  className="hover:text-pink-400 transition-colors cursor-pointer"
                >
                  Membership
                </button>
              </li>
              <li>
                <button
                  onClick={() => {
                    if (currentUser) {
                      onOpenProfile?.('wallet');
                    } else {
                      onOpenAuth?.('login');
                    }
                  }}
                  className="text-emerald-400 hover:text-emerald-300 font-bold transition-colors cursor-pointer flex items-center gap-1.5"
                >
                  <span>💰 Earnings Wallet</span>
                  {!currentUser && (
                    <span className="text-[10px] bg-slate-800 text-slate-400 border border-slate-700 px-1.5 py-0.5 rounded-sm">
                      Login
                    </span>
                  )}
                </button>
              </li>
              <li>
                <button
                  onClick={() => {
                    if (currentUser) {
                      onOpenProfile?.('profile');
                    } else {
                      onOpenAuth?.('login');
                    }
                  }}
                  className="hover:text-pink-400 transition-colors cursor-pointer"
                >
                  Profile & Photo
                </button>
              </li>
            </ul>
          </div>

          {/* Company (2 cols) */}
          <div className="lg:col-span-2 space-y-3">
            <h4 className="font-bold text-white text-sm tracking-wider uppercase">
              Company
            </h4>
            <ul className="space-y-2 text-xs sm:text-sm text-slate-400">
              <li>
                <button
                  onClick={() => {
                    const el = document.getElementById('about-section');
                    if (el) {
                      el.scrollIntoView({ behavior: 'smooth' });
                    } else if (onOpenAbout) {
                      onOpenAbout();
                    }
                  }}
                  className="hover:text-pink-400 transition-colors cursor-pointer text-left"
                >
                  About Us
                </button>
              </li>
              <li>
                <button
                  onClick={() => onNavClick('blog')}
                  className="hover:text-pink-400 transition-colors cursor-pointer"
                >
                  Blog
                </button>
              </li>
              <li>
                <a
                  href={`mailto:${officialEmail}?subject=Career%20Inquiry%20-%20PairUpToday`}
                  className="hover:text-pink-400 transition-colors"
                >
                  Careers
                </a>
              </li>
              <li>
                <button
                  type="button"
                  id="footer-contact-us-btn"
                  onClick={() => setIsContactOpen(true)}
                  className="hover:text-pink-400 transition-colors cursor-pointer text-left flex items-center gap-1.5"
                  title="Contact Us via email pairuptoday@gmail.com"
                >
                  <Mail className="w-3.5 h-3.5 text-pink-400" />
                  <span>Contact Us</span>
                </button>
              </li>
            </ul>
          </div>

          {/* Support (2 cols) */}
          <div className="lg:col-span-2 space-y-3">
            <h4 className="font-bold text-white text-sm tracking-wider uppercase">
              Support
            </h4>
            <ul className="space-y-2 text-xs sm:text-sm text-slate-400">
              <li>
                <button
                  type="button"
                  id="footer-help-center-btn"
                  onClick={() => setIsContactOpen(true)}
                  className="hover:text-pink-400 transition-colors cursor-pointer text-left"
                >
                  Help Center
                </button>
              </li>
              <li>
                <button
                  onClick={onOpenSafety}
                  className="hover:text-pink-400 transition-colors text-emerald-400 font-semibold cursor-pointer"
                >
                  Safety Tips
                </button>
              </li>
              <li>
                <a href="#terms" onClick={(e) => { e.preventDefault(); onOpenSafety(); }} className="hover:text-pink-400 transition-colors">
                  Terms & Conditions
                </a>
              </li>
              <li>
                <a href="#privacy" onClick={(e) => { e.preventDefault(); onOpenSafety(); }} className="hover:text-pink-400 transition-colors">
                  Privacy Policy
                </a>
              </li>
              <li>
                <button
                  type="button"
                  onClick={() => {
                    if (onOpenPermissions) onOpenPermissions();
                    else onOpenSafety();
                  }}
                  className="hover:text-emerald-400 text-emerald-400/90 transition-colors flex items-center gap-1.5 cursor-pointer"
                >
                  <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
                  <span>Privacy & Consent Hub</span>
                </button>
              </li>
            </ul>
          </div>

          {/* Newsletter (2 cols) matching screenshot */}
          <div className="lg:col-span-2 space-y-3">
            <h4 className="font-bold text-white text-sm tracking-wider uppercase">
              Newsletter
            </h4>
            <p className="text-xs text-slate-400">
              Subscribe to get updates and offers
            </p>

            <form onSubmit={handleSubscribe} className="relative mt-2">
              <input
                id="newsletter-email-input"
                type="email"
                required
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-3 pr-10 py-2.5 bg-slate-900/90 border border-slate-700/80 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-none focus:border-pink-500"
              />
              <button
                id="newsletter-submit-btn"
                type="submit"
                className="absolute right-1 top-1 bottom-1 w-8 bg-pink-500 hover:bg-pink-600 text-white rounded-lg flex items-center justify-center transition-colors cursor-pointer"
                title="Subscribe"
              >
                <Send className="w-3.5 h-3.5" />
              </button>
            </form>

            {subscribed && (
              <p className="text-[11px] text-pink-400 font-medium animate-pulse">
                ✓ Thanks for subscribing!
              </p>
            )}
          </div>

        </div>

        {/* Bottom Copyright matching screenshot */}
        <div className="pt-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-slate-500">
          <p>© {new Date().getFullYear()} PairUpToday. All rights reserved.</p>
          <div className="flex items-center gap-3 sm:gap-4 flex-wrap">
            <span className="flex items-center gap-1 text-emerald-400">
              <ShieldCheck className="w-3.5 h-3.5" /> 100% Escrow Protection
            </span>
            <span>•</span>
            <Link to="/login" className="hover:text-pink-400 text-slate-400 transition-colors">
              User Portal
            </Link>
          </div>
        </div>

      </div>

      {/* Contact Us Modal */}
      {isContactOpen && (
        <div
          id="contact-us-modal"
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/75 backdrop-blur-xs"
          onClick={() => setIsContactOpen(false)}
        >
          <div
            className="bg-white rounded-3xl max-w-md w-full overflow-hidden shadow-2xl border border-slate-200 text-slate-800 animate-in fade-in zoom-in-95 duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-pink-600 via-purple-600 to-indigo-700 text-white p-5 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-xl bg-white/15 flex items-center justify-center backdrop-blur-xs">
                  <Mail className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-extrabold text-base tracking-tight leading-tight">Contact PairUpToday</h3>
                  <p className="text-[11px] text-pink-100 mt-0.5">24/7 Client & Companion Support</p>
                </div>
              </div>
              <button
                type="button"
                id="close-contact-modal"
                onClick={() => setIsContactOpen(false)}
                className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors cursor-pointer"
                title="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-5 sm:p-6 space-y-4">
              <div className="space-y-1.5 text-center">
                <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Official Support Email</span>
                <p className="text-xs text-slate-600">
                  Have questions about bookings, companions, escrow, or safety? Write directly to our team:
                </p>
              </div>

              {/* Email Card with 1-Click Copy & Mailto */}
              <div className="bg-slate-50 rounded-2xl border border-slate-200/90 p-4 space-y-3">
                <div className="flex items-center justify-between gap-2 bg-white px-3.5 py-2.5 rounded-xl border border-slate-200 shadow-2xs">
                  <div className="flex items-center gap-2 min-w-0">
                    <Mail className="w-4 h-4 text-pink-600 shrink-0" />
                    <span className="font-mono text-sm font-bold text-slate-900 truncate select-all">
                      {officialEmail}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={handleCopyEmail}
                    className="p-1.5 hover:bg-pink-50 rounded-lg text-pink-600 transition-colors cursor-pointer shrink-0"
                    title="Copy Email"
                  >
                    {copiedEmail ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={handleCopyEmail}
                    className="py-2 px-3 rounded-xl border border-slate-200 bg-white hover:bg-slate-100 text-xs font-bold text-slate-700 flex items-center justify-center gap-1.5 shadow-2xs transition-colors cursor-pointer"
                  >
                    {copiedEmail ? (
                      <>
                        <Check className="w-3.5 h-3.5 text-emerald-600" />
                        <span className="text-emerald-700">Copied!</span>
                      </>
                    ) : (
                      <>
                        <Copy className="w-3.5 h-3.5" />
                        <span>Copy Email</span>
                      </>
                    )}
                  </button>

                  <a
                    href={`mailto:${officialEmail}?subject=Support%20Inquiry%20-%20PairUpToday`}
                    className="py-2 px-3 rounded-xl bg-pink-600 hover:bg-pink-700 text-white text-xs font-bold flex items-center justify-center gap-1.5 shadow-sm transition-colors cursor-pointer"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    <span>Send Email</span>
                  </a>
                </div>
              </div>

              {/* Official Social Channels */}
              <div className="pt-2 border-t border-slate-100 space-y-2">
                <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider block">
                  Official Social Channels
                </span>
                <div className="grid grid-cols-2 gap-2">
                  <a
                    href="https://www.facebook.com/share/19Xw6KFEKU/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 p-2.5 rounded-xl bg-slate-50 hover:bg-blue-50 border border-slate-200 text-slate-700 hover:text-blue-700 text-xs font-semibold transition-colors"
                  >
                    <Facebook className="w-4 h-4 text-[#1877F2]" />
                    <span>Facebook</span>
                  </a>
                  <a
                    href="https://www.instagram.com/pairuptoday?stkn=MXBtNXhreTBnMWhuNA=="
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 p-2.5 rounded-xl bg-slate-50 hover:bg-pink-50 border border-slate-200 text-slate-700 hover:text-pink-700 text-xs font-semibold transition-colors"
                  >
                    <Instagram className="w-4 h-4 text-pink-600" />
                    <span>Instagram</span>
                  </a>
                </div>
              </div>

              {/* Support Response note */}
              <div className="bg-emerald-50 rounded-xl p-3 border border-emerald-100 flex items-start gap-2 text-[11px] text-emerald-800">
                <ShieldCheck className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                <span>
                  Our support team is active 24/7. All client and companion inquiries receive responses within 2 to 4 hours.
                </span>
              </div>
            </div>

            {/* Modal Bottom Bar */}
            <div className="p-3 bg-slate-50 border-t border-slate-100 text-center">
              <button
                type="button"
                onClick={() => setIsContactOpen(false)}
                className="w-full py-2.5 bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold rounded-xl transition-colors cursor-pointer"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </footer>
  );
};
